import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useChallenge } from '../hooks/challenge/useChallenge'
import { useChallengeId } from '../hooks/challenge/useChallengeId'
import { useChallengeAccess } from '../hooks/membership/useChallengeAccess'
import {
  challengePaymentIntroPath,
  challengePaymentSuccessPath,
  challengeTeamFreeAgentPath,
  challengeTeamPath,
  ScreenPath,
} from '../navigation'
import { PassType } from '../service/domain/PaymentService'
import SimplePageLayout from '../ui/layout/SimplePageLayout'
import LoadingIndicator from '../ui/util/LoadingIndicator'
import Button from '../ui/primitives/Button'
import Eyebrow from '../ui/primitives/Eyebrow'
import styles from '../ui/team/TeamEntry.module.css'

const STEPS = [
  'Buy your individual pass',
  'We place you on a team before the challenge begins',
  'You get an email as soon as your team is ready',
]

export default function FreeAgentScreen() {
  const navigate = useNavigate()
  const challengeId = useChallengeId()
  const { data: challenge, isLoading: isLoadingChallenge } = useChallenge(challengeId)
  const { teamID, isFreeAgent, isEntitled } = useChallengeAccess(challengeId)
  const allowsFreeAgent = challenge?.allowsFreeAgent === true

  useEffect(() => {
    if (isLoadingChallenge) return
    if (teamID) {
      navigate(`${ScreenPath.team}/${teamID}`, { replace: true })
    } else if (isFreeAgent) {
      navigate(ScreenPath.home, { replace: true })
    } else if (challengeId && !allowsFreeAgent) {
      navigate(challengeTeamPath(challengeId), { replace: true })
    }
  }, [isLoadingChallenge, teamID, isFreeAgent, allowsFreeAgent, challengeId, navigate])

  if (isLoadingChallenge || (challengeId && !teamID && !isFreeAgent && !allowsFreeAgent)) {
    return <LoadingIndicator />
  }

  function onContinue() {
    const action = encodeURIComponent(JSON.stringify({ type: 'FREE_AGENT', payload: {} }))
    if (isEntitled) {
      navigate(
        `${challengePaymentSuccessPath(challengeId)}?passType=${PassType.INDIVIDUAL}&action=${action}`,
      )
    } else {
      navigate(
        `${challengePaymentIntroPath(challengeId)}?sourcePath=${encodeURIComponent(
          challengeTeamFreeAgentPath(challengeId),
        )}&action=${action}`,
      )
    }
  }

  return (
    <SimplePageLayout
      title="Free agent"
      description="No team yet? Sign up solo and we'll match you with a squad before the challenge starts."
      backTo={challengeTeamPath(challengeId)}
    >
      <div className={styles.stack}>
        <div className={styles.panel}>
          <Eyebrow>How it works</Eyebrow>
          <ol className={styles.steps}>
            {STEPS.map((text, index) => (
              <li key={text} className={styles.step}>
                <span className={styles.stepIndex}>{index + 1}</span>
                <span className={styles.stepText}>{text}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className={styles.actions}>
          <Button onClick={onContinue}>Continue</Button>
        </div>
      </div>
    </SimplePageLayout>
  )
}
